import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, ListGroup, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import StatCard from '../components/common/StatCard';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import PatientSearch from '../components/common/PatientSearch';
import { reportApi, visitApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { useClinicEvent } from '../context/SocketContext';
import { formatDate, formatMoney, formatTime } from '../utils/format';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, can, isDoctor } = useAuth();

  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [statsRes, queueRes] = await Promise.all([visitApi.todayStats(), visitApi.queue()]);
      setStats(statsRes.data);
      setQueue(queueRes.data.filter((v) => v.status !== 'cancelled'));

      if (isDoctor) {
        const fu = await reportApi.followUps({ days: 7 });
        setFollowUps(fu.data);
      }
    } catch {
      // errors surface on the pages that own the data; the dashboard stays quiet
    } finally {
      setLoading(false);
    }
  }, [isDoctor]);

  useEffect(() => {
    load();
  }, [load]);

  useClinicEvent(['queue:changed', 'consultation:saved'], load);

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const greetingIcon =
    hour < 12 ? 'bi-sunrise-fill' : hour < 17 ? 'bi-sun-fill' : 'bi-moon-stars-fill';
  const weekday = now.toLocaleDateString('en-GB', { weekday: 'long' });

  if (loading) return <Loader label="Loading today's numbers..." className="py-5" />;

  const nextUp = queue.filter((v) => v.status === 'waiting').slice(0, 5);
  const current = queue.find((v) => v.status === 'in-consultation');

  return (
    <>
      <div className="greet-banner">
        <div className="greet-main">
          <span className="greet-icon">
            <i className={`bi ${greetingIcon}`} />
          </span>

          <div className="min-w-0">
            <div className="greet-hello">{greeting},</div>
            <h1 className="greet-name">{user.name}</h1>
            <div className="greet-date">
              <i className="bi bi-calendar3" />
              {formatDate(now)}, {weekday}
            </div>
          </div>
        </div>

        <div className="greet-actions">
          {can('reception') && (
            <Button className="btn-on-banner" onClick={() => navigate('/patients/new')}>
              <i className="bi bi-person-plus me-1" /> New Patient
            </Button>
          )}
          {/* Whichever button stands alone carries the solid white fill. */}
          <Button
            className={can('reception') ? 'btn-on-banner-ghost' : 'btn-on-banner'}
            onClick={() => navigate('/queue')}
          >
            <i className="bi bi-people-fill me-1" /> Full Queue
            <i className="bi bi-chevron-right ms-2 small"  style={{ WebkitTextStroke: "1px currentColor" }} />
          </Button>
        </div>
      </div>

      <Card className="search-panel mb-4">
        <Card.Body>
          <div className="search-hint">
            <i className="bi bi-search me-2" style={{ color: 'var(--clinic-primary)' }} />
            Look up a patient by name, mobile number or patient ID
          </div>
          <PatientSearch autoFocus onSelect={(p) => navigate(`/patients/${p._id}`)} />
        </Card.Body>
      </Card>

      <Row className="g-4 mb-4">
        <Col xs={6} lg={3}>
          <StatCard label="Patients Today" value={stats.total} icon="bi-people-fill" color="#2f4bb5" />
        </Col>
        <Col xs={6} lg={3}>
          <StatCard label="Still Waiting" value={stats.waiting} icon="bi-hourglass-split" color="#d9a213" />
        </Col>
        <Col xs={6} lg={3}>
          <StatCard label="Completed" value={stats.completed} icon="bi-check-circle-fill" color="#1f9d61" />
        </Col>
        <Col xs={6} lg={3}>
          <StatCard
            label="New Registrations"
            value={stats.newPatients}
            icon="bi-person-plus-fill"
            color="#e0563f"
          />
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={7}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-list-ol me-2" />
                Who's next
              </span>
              <Button size="sm" variant="light" onClick={() => navigate('/queue')}>
                See all
              </Button>
            </Card.Header>

            {current && (
              <div className="px-3 pt-3">
                <div className="d-flex align-items-center gap-3 p-2 rounded" style={{ background: '#fff7ed' }}>
                  <div className="token-badge" style={{ background: 'var(--clinic-accent)' }}>
                    {current.tokenNo}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">{current.patient.name}</div>
                    <div className="small text-muted">Currently with the doctor</div>
                  </div>
                  {isDoctor && (
                    <Button size="sm" className="btn-clinic" onClick={() => navigate(`/consultation/${current._id}`)}>
                      Continue
                    </Button>
                  )}
                </div>
              </div>
            )}

            {nextUp.length === 0 ? (
              <EmptyState icon="bi-emoji-smile" title="Nobody is waiting" hint="The queue is clear right now." />
            ) : (
              <ListGroup variant="flush">
                {nextUp.map((v) => (
                  <ListGroup.Item
                    key={v._id}
                    className="d-flex align-items-center gap-3 queue-row"
                    onClick={() => navigate(isDoctor ? `/consultation/${v._id}` : `/patients/${v.patient._id}`)}
                  >
                    <div className="token-badge">{v.tokenNo}</div>

                    <div className="flex-grow-1 min-w-0">
                      <div className="list-name">
                        {v.patient.name}
                        {v.visitType === 'new' && (
                          <Badge bg="success" className="ms-2" style={{ fontSize: '0.62rem' }}>
                            NEW
                          </Badge>
                        )}
                      </div>
                      <div className="list-sub">
                        {v.patient.patientId} &middot; {v.patient.mobile}
                      </div>
                    </div>

                    <span className="list-time">
                      <i className="bi bi-clock" />
                      {formatTime(v.visitDate)}
                    </span>
                    <i className="bi bi-chevron-right list-chevron" />
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}

            {stats.total > 0 && (
              <div className="p-3 mt-auto">
                <div className="encourage-box">
                  <span className="encourage-icon">
                    <i className={`bi ${stats.waiting > 0 ? 'bi-calendar2-check' : 'bi-check2-circle'}`} />
                  </span>
                  <div>
                    <div className="encourage-title">
                      {stats.waiting > 0 ? "Keep going! You're doing great." : 'All caught up.'}
                    </div>
                    <div className="encourage-sub">
                      <strong>{stats.waiting > 0 ? stats.waiting : stats.completed}</strong>{' '}
                      {stats.waiting > 0
                        ? `still waiting of ${stats.total} today.`
                        : `patient${stats.completed === 1 ? '' : 's'} seen today.`}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </Col>

        <Col lg={5}>
          {(isDoctor) && (
            <Card className="mb-4">
              <Card.Header>
                <i className="bi bi-calendar-check me-2" />
                Follow-ups due this week
              </Card.Header>
              {followUps.length === 0 ? (
                <EmptyState icon="bi-calendar" title="No follow-ups due" />
              ) : (
                <>
                  <ListGroup variant="flush">
                    {followUps.slice(0, 5).map((f) => (
                      <ListGroup.Item
                        key={f._id}
                        className="d-flex align-items-center gap-3 queue-row"
                        onClick={() => f.patient && navigate(`/patients/${f.patient._id}`)}
                      >
                        <div className="flex-grow-1 min-w-0">
                          <div className="list-name">{f.patient?.name || 'Patient'}</div>
                          <div className="list-sub">
                            {f.diagnosis?.join(', ') || 'Review'} &middot; {f.patient?.mobile}
                          </div>
                        </div>
                        <Badge bg="info">{formatDate(f.followUpDate)}</Badge>
                        <i className="bi bi-chevron-right list-chevron" />
                      </ListGroup.Item>
                    ))}
                  </ListGroup>

                  <button type="button" className="card-link-foot" onClick={() => navigate('/reports')}>
                    View all follow-ups
                    <i className="bi bi-arrow-right ms-2" />
                  </button>
                </>
              )}
            </Card>
          )}

          {can('reception') && (
            <Card>
              <Card.Header>
                <i className="bi bi-cash-coin me-2" />
                Today's Collection
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Billed</span>
                  <span className="fw-semibold">{formatMoney(stats.billed)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Received</span>
                  <span className="fw-semibold text-success">{formatMoney(stats.collected)}</span>
                </div>
                <div className="d-flex justify-content-between border-top pt-2">
                  <span className="text-muted">Pending</span>
                  <span className={`fw-semibold ${stats.pending > 0 ? 'text-danger' : 'text-muted'}`}>
                    {formatMoney(stats.pending)}
                  </span>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </>
  );
}
